import type {
  WithContext,
  Product,
  Offer,
  PropertyValue,
  OfferItemCondition,
} from 'schema-dts'

import type { SearchResult } from '@/queries/listingSearchQuery'

import { mapFilterToSchemaOrg } from './filterMappings'

/**
 * Generates Product JSON-LD schema for a listing
 * @param listing The search result listing
 * @param baseUrl The base URL for the site
 * @returns Product schema object
 */
export const generateProductSchema = (
  listing: SearchResult,
  baseUrl: string
): WithContext<Product> => {
  // Get the primary condition (first one if multiple)
  const primaryCondition = listing.displayedCondition || 'Used'

  // Get current price from auction data or fallback
  const currentPrice =
    listing.auction3MP?.winningBidAmount ||
    listing.auctionEnt?.winningBidAmount ||
    Number(listing.winningBidAmount) ||
    0

  // Get listing URL
  const listingUrl = listing.auction3MP
    ? `${baseUrl}/buy/listings/details/${listing.auction3MP.listingId}`
    : listing.auctionUrl || `${baseUrl}/all-auctions`

  // Build additional properties
  const additionalProperties: PropertyValue[] = []

  // Add detailed condition as additional property
  if (listing.displayedCondition) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Detailed Condition',
      value: listing.displayedCondition,
    })
  }

  // Add region if available
  if (listing.region) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Location',
      value: listing.region,
    })
  }

  // Add inventory type if available
  if (listing.inventoryType) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Inventory Type',
      value: listing.inventoryType,
    })
  }

  // Add listing type if available
  additionalProperties.push({
    '@type': 'PropertyValue',
    name: 'Listing Type',
    value: listing.listingType,
  })

  // Add inspection if available
  additionalProperties.push({
    '@type': 'PropertyValue',
    name: 'Inspection',
    // API client says inspection is now required, but the API client update
    // that caused this change wasn't done in the same context that changed
    // it from an optional string to a required array, so it was unknown if
    // it truly can't be omitted now, so added support for inspection being
    // omitted for added safety.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    value: listing.inspection?.[0],
  })

  // Add grade if available (for device grades)
  if (primaryCondition.includes('Grade')) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Grade',
      value: primaryCondition,
    })
  }

  // Add number of bids as units indicator
  if (listing.numberOfBids) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Bids',
      value: listing.numberOfBids.toString(),
    })
  }

  // Build the offer
  const offer: Offer = {
    '@type': 'Offer',
    priceCurrency: listing.currency || 'USD',
    price: currentPrice,
    availability: 'https://schema.org/InStock',
    itemCondition: mapFilterToSchemaOrg(
      'condition',
      primaryCondition
    ) as OfferItemCondition,
    url: listingUrl,
  }

  // Add additional properties to offer if any
  if (additionalProperties.length > 0) {
    offer.additionalProperty = additionalProperties
  }

  // Get the primary category and map it to Schema.org
  // TODO(noUncheckedIndexedAccess): categories may be empty, fallback to 'General'
  const primaryCategory = listing.categories[0] ?? 'General'
  const mappedCategory = mapFilterToSchemaOrg('category', primaryCategory)

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title,
    image:
      listing.primaryImageUrl ||
      `${baseUrl}/home-portal/images/fallbackListingImage.png`,
    category: mappedCategory,
    offers: offer,
  }
}

/**
 * Generates Product JSON-LD schema for multiple listings
 * @param listings Array of search result listings (first 3 will be used)
 * @param baseUrl The base URL for the site
 * @returns Array of Product schema objects
 */
export const generateProductSchemas = (
  listings: SearchResult[],
  baseUrl: string
): WithContext<Product>[] => {
  return listings
    .slice(0, 3) // Only use first 3 listings
    .map((listing) => generateProductSchema(listing, baseUrl))
}
